import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Activity,
  Bitcoin,
  TrendingUp,
  TrendingDown,
  Minus,
  Zap,
  Lock,
  Cpu,
  RefreshCw,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  Rocket,
  Coins,
  BarChart3,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Icon mapping for different feed item types
const getIcon = (type, iconName) => {
  const iconClass = "w-4 h-4";
  
  if (type === 'arbitrage_alert') return <Zap className={`${iconClass} text-yellow-400`} />;
  if (type === 'price_update') return <Bitcoin className={`${iconClass} text-orange-400`} />;
  if (type === 'ratio_update') return <TrendingUp className={`${iconClass} text-blue-400`} />;
  if (type === 'tvl_update') return <Lock className={`${iconClass} text-green-400`} />;
  if (type === 'm2m_activity') return <Cpu className={`${iconClass} text-purple-400`} />;
  if (type === 'token_stats') return <Coins className={`${iconClass} text-amber-400`} />;
  if (type?.includes('deposit')) return <ArrowDownRight className={`${iconClass} text-green-400`} />;
  if (type?.includes('withdraw')) return <ArrowUpRight className={`${iconClass} text-red-400`} />;
  if (type?.includes('agent')) return <Rocket className={`${iconClass} text-cyan-400`} />;
  
  return <Activity className={`${iconClass} text-zinc-400`} />;
};

// Get background color for feed item type
const getItemStyle = (type, priority) => {
  if (priority === 'high' || type === 'arbitrage_alert') {
    return 'bg-yellow-500/10 border-l-2 border-yellow-500';
  }
  if (type === 'm2m_activity') {
    return 'bg-purple-500/5 border-l-2 border-purple-500/50';
  }
  if (type === 'tvl_update') {
    return 'bg-green-500/5';
  }
  if (type === 'price_update') {
    return 'bg-orange-500/5';
  }
  return 'hover:bg-zinc-800/50';
};

// Trend indicator component
const TrendIndicator = ({ trend }) => {
  if (trend === 'increasing') {
    return (
      <span className="flex items-center gap-1 text-green-400 text-xs">
        <TrendingUp className="w-3 h-3" />
        Up
      </span>
    );
  }
  if (trend === 'decreasing') {
    return (
      <span className="flex items-center gap-1 text-red-400 text-xs">
        <TrendingDown className="w-3 h-3" />
        Down
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1 text-zinc-500 text-xs">
      <Minus className="w-3 h-3" />
      Stable
    </span>
  );
};

export default function MarketIntelligenceFeed({ 
  compact = false, 
  maxItems = 10,
  showHeader = true,
  showRefresh = true,
  className = ""
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/api/market/intelligence`);
      // Guard: bail out on non-JSON 5xx (Cloudflare 520) so the browser
      // never throws "The string did not match the expected pattern".
      if (!response.ok || !(response.headers.get("content-type") || "").includes("json")) {
        return;
      }
      const result = await response.json();
      
      if (result.success) {
        setData(result);
        setLastUpdated(new Date());
      }
    } catch (error) {
      console.error('Market intelligence fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Refresh every 60 seconds
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const feed = data?.feed?.slice(0, maxItems) || [];

  if (compact) {
    // Compact version for landing page
    return (
      <div className={`bg-zinc-900/80 backdrop-blur-sm border border-zinc-800 rounded-xl ${className}`}>
        {showHeader && (
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
            <div className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-orange-400" />
              <span className="text-sm font-semibold text-white">MARKET INTELLIGENCE</span>
            </div>
            {showRefresh && (
              <Button
                variant="ghost"
                size="sm"
                onClick={fetchData}
                disabled={loading}
                className="h-7 px-2"
              >
                <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            )}
          </div>
        )}
        
        <ScrollArea className="h-[300px]">
          {loading && !data ? (
            <div className="p-8 text-center">
              <RefreshCw className="w-6 h-6 text-zinc-500 mx-auto mb-2 animate-spin" />
              <p className="text-sm text-zinc-500">Loading market data...</p>
            </div>
          ) : feed.length === 0 ? (
            <div className="p-8 text-center">
              <Activity className="w-6 h-6 text-zinc-600 mx-auto mb-2" />
              <p className="text-sm text-zinc-500">No market activity yet</p>
            </div>
          ) : (
            <div className="divide-y divide-zinc-800/50">
              {feed.map((item, index) => (
                <div
                  key={index}
                  className={`px-4 py-3 transition-colors ${getItemStyle(item.type, item.priority)}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-lg bg-zinc-800 flex items-center justify-center flex-shrink-0">
                      {getIcon(item.type, item.icon)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">{item.title}</p>
                      <p className="text-xs text-zinc-400 truncate">{item.description}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Clock className="w-3 h-3 text-zinc-500" />
                        <span className="text-xs text-zinc-500 font-mono">
                          {new Date(item.timestamp).toLocaleTimeString()}
                        </span>
                        {item.data?.trend && <TrendIndicator trend={item.data.trend} />}
                        {item.priority === 'high' && (
                          <Badge variant="outline" className="text-xs text-yellow-400 border-yellow-400/50">
                            ALERT
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
        
        {lastUpdated && (
          <div className="px-4 py-2 border-t border-zinc-800 text-xs text-zinc-500 text-center">
            Last updated: {lastUpdated.toLocaleTimeString()}
          </div>
        )}
      </div>
    );
  }

  // Full version with stats cards
  return (
    <Card className={`bg-zinc-900/80 backdrop-blur-sm border-zinc-800 ${className}`}>
      {showHeader && (
        <CardHeader className="border-b border-zinc-800 pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-bold text-white flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-orange-400" />
              MARKET INTELLIGENCE
            </CardTitle>
            {showRefresh && (
              <Button
                variant="ghost"
                size="sm"
                onClick={fetchData}
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            )}
          </div>
        </CardHeader>
      )}
      
      <CardContent className="p-0">
        {/* Quick Stats Row */}
        {data && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 p-4 border-b border-zinc-800 bg-zinc-800/30">
            <div className="text-center">
              <p className="text-xs text-zinc-500">BTC</p>
              <p className="text-sm font-bold text-orange-400">
                ${data.prices?.btc?.toLocaleString() || '—'}
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs text-zinc-500">STX</p>
              <p className="text-sm font-bold text-cyan-400">
                ${data.prices?.stx?.toFixed(4) || '—'}
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs text-zinc-500">sBTC/BTC</p>
              <p className={`text-sm font-bold ${
                data.arbitrage?.opportunity_detected ? 'text-yellow-400' : 'text-green-400'
              }`}>
                {data.ratios?.sbtc_to_btc?.toFixed(4) || '—'}
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs text-zinc-500">M2M Today</p>
              <p className="text-sm font-bold text-purple-400">
                {data.m2m?.today_requests || 0}
              </p>
            </div>
          </div>
        )}
        
        {/* Feed */}
        <ScrollArea className="h-[350px]">
          {loading && !data ? (
            <div className="p-12 text-center">
              <RefreshCw className="w-8 h-8 text-zinc-500 mx-auto mb-2 animate-spin" />
              <p className="text-sm text-zinc-500">Loading market intelligence...</p>
            </div>
          ) : feed.length === 0 ? (
            <div className="p-12 text-center">
              <Activity className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
              <p className="text-sm text-zinc-500">No market activity yet</p>
            </div>
          ) : (
            <div className="divide-y divide-zinc-800/50">
              {feed.map((item, index) => (
                <div
                  key={index}
                  className={`px-4 py-3 transition-colors ${getItemStyle(item.type, item.priority)}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-zinc-800 flex items-center justify-center flex-shrink-0">
                      {getIcon(item.type, item.icon)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white">{item.title}</p>
                      <p className="text-xs text-zinc-400 mt-0.5">{item.description}</p>
                      <div className="flex items-center gap-3 mt-1.5">
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3 text-zinc-500" />
                          <span className="text-xs text-zinc-500 font-mono">
                            {new Date(item.timestamp).toLocaleTimeString()}
                          </span>
                        </div>
                        {item.data?.sbtc_trend && <TrendIndicator trend={item.data.sbtc_trend} />}
                        {item.priority === 'high' && (
                          <Badge className="text-xs bg-yellow-500/20 text-yellow-400 border-yellow-400/50">
                            ARBITRAGE
                          </Badge>
                        )}
                        {item.type === 'm2m_activity' && (
                          <Badge className="text-xs bg-purple-500/20 text-purple-400">
                            M2M
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
        
        {lastUpdated && (
          <div className="px-4 py-2 border-t border-zinc-800 text-xs text-zinc-500 text-center">
            Auto-refresh every 60s • Last: {lastUpdated.toLocaleTimeString()}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
